import { friendlyError } from "@/lib/friendlyError";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

const CATEGORIES = [
  { value: "not_working", label: "Something is not working" },
  { value: "data_incorrect", label: "Data looks incorrect" },
  { value: "feature_broken", label: "A feature is missing or broken" },
  { value: "confusing", label: "Something feels confusing" },
  { value: "other", label: "Other" },
];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Track previous routes for breadcrumb context
const recentRoutes: string[] = [];
export function trackRoute(path: string) {
  if (recentRoutes[recentRoutes.length - 1] !== path) {
    recentRoutes.push(path);
    if (recentRoutes.length > 5) recentRoutes.shift();
  }
}

export function ReportIssueModal({ open, onOpenChange }: Props) {
  const { user, role } = useAuth();
  const location = useLocation();
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [includeContext, setIncludeContext] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setCategory("");
    setDescription("");
    setIncludeContext(true);
  };

  const handleSubmit = async () => {
    if (!category || !description.trim()) {
      toast.error("Please choose a category and describe the issue.");
      return;
    }
    setSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke("send-issue-report", {
        body: {
          category,
          description: description.trim(),
          route: location.pathname,
          browser: includeContext ? navigator.userAgent : "",
          previous_routes: includeContext ? recentRoutes.slice(-3, -1) : [],
        },
      });
      if (error) throw error;
      toast.success("Report sent. Thank you, we will look into this.", {
        description: "If urgent, email support@nursly.co.uk directly.",
      });
      reset();
      onOpenChange(false);
    } catch (e: any) {
      toast.error("Could not send your report", { description: friendlyError(e) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!submitting) onOpenChange(o); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-nursly-forest">Report an issue</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="font-body">What happened?</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger><SelectValue placeholder="Select a category" /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="font-body">Describe the issue</Label>
            <Textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="What were you trying to do, and what happened instead?"
              rows={5}
              maxLength={2000}
            />
          </div>
          <div className="flex items-start gap-2">
            <Checkbox id="ctx" checked={includeContext} onCheckedChange={(v) => setIncludeContext(!!v)} />
            <Label htmlFor="ctx" className="font-body text-sm text-nursly-muted cursor-pointer leading-snug">
              Include page and browser details (helps us fix things faster)
            </Label>
          </div>
          <p className="text-xs text-nursly-muted font-body">
            We read every report. If urgent, email <a href="mailto:support@nursly.co.uk" className="underline">support@nursly.co.uk</a> directly.
          </p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !category || !description.trim()}
            style={{ background: "#B45309" }}
            className="text-white"
          >
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Send report
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
